import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import SearchBar from '../../Components/utils/SearchBar';
import Dropdown from '../../Components/utils/Dropdown';
import axios from 'axios';
import Loader from '../../Components/utils/Loader';
import ProductCard from '../../Components/buyer/ProductCard';
import { transformProduct } from '../../data/constants';
import { CategoryContext } from '../../Contexts/CategoryContext';
import { LoginContext } from '../../Contexts/LoginContext';
import countries from '../../data/countries.json';
import SEO from '../../Components/utils/SEO';
import { savedBuyerPoint, getBrowserLocation } from '../../utils/buyerLocation';

const MAX_LOCAL_KM = 1000;
const DISTANCE_PRESETS = [10, 25, 50, 100, 250, 500, 1000];
const SCOPE_OPTIONS = [
    { value: 'local', label: 'Local' },
    { value: 'international', label: 'International' },
];

const Products = ({ forceB2B = false }) => {
    const [searchParams] = useSearchParams();
    const isB2BMode = forceB2B || searchParams.get('market') === 'b2b';
    const initialQuery = searchParams.get('query') || searchParams.get('q') || '';
    const initialFilters = {
        category: searchParams.get('category') || '',
        subcategory: searchParams.get('subcategory') || '',
        childcategory: searchParams.get('childcategory') || '',
        minPrice: searchParams.get('minPrice') || '',
        maxPrice: searchParams.get('maxPrice') || '',
        deliveryType: searchParams.get('deliveryType')?.split(',') || [],
        minRating: searchParams.get('minRating') || '',
        sellerCountry: searchParams.get('sellerCountry') || '',
        maxDistance: searchParams.get('maxDistance') || '',
        scope: searchParams.get('scope') || '',
    };

    const [searchQuery, setSearchQuery] = useState(initialQuery);
    const [filters, setFilters] = useState(initialFilters);
    const [products, setProducts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [b2bCategories, setB2bCategories] = useState([]);
    const { categories } = useContext(CategoryContext);
    const { user, currency, exchangeRate } = useContext(LoginContext);
    const [sort, setSort] = useState(searchParams.get('sort') || 'recommended');
    const [browserPoint, setBrowserPoint] = useState(null);
    const [priceDraft, setPriceDraft] = useState({ min: initialFilters.minPrice, max: initialFilters.maxPrice });
    const [distanceDraft, setDistanceDraft] = useState(Number(initialFilters.maxDistance) || MAX_LOCAL_KM);
    const buyerPoint = savedBuyerPoint(user) || browserPoint;
    const buyerCountry = user?.buyerAddress?.country?.name || '';
    const categorySource = isB2BMode ? b2bCategories : categories;

    const fetchProducts = async (overrideQuery) => {
        setIsLoading(true);
        const params = new URLSearchParams();
        const activeQuery = overrideQuery !== undefined ? overrideQuery : searchQuery;

        if (isB2BMode) {
            if (activeQuery) params.set('q', activeQuery);
            if (filters.category) params.set('category', filters.category);
            if (filters.subcategory) params.set('subcategory', filters.subcategory);
            if (filters.childcategory) params.set('childcategory', filters.childcategory);
            if (filters.sellerCountry) params.set('country', filters.sellerCountry);
            if (filters.minPrice) params.set('minMoq', filters.minPrice);
            if (filters.maxPrice) params.set('maxMoq', filters.maxPrice);
            // Pass buyer's country so backend can filter by visibility
            if (buyerCountry) params.set('buyerCountry', buyerCountry);
        } else {
            if (activeQuery) params.set('query', activeQuery);
            ['category', 'subcategory', 'childcategory', 'minRating', 'sellerCountry', 'scope'].forEach((key) => {
                if (filters[key]) params.set(key, filters[key]);
            });
            // Listing prices are stored in USD; the buyer types them in their own currency.
            const rate = Number(exchangeRate) || 1;
            if (filters.minPrice) params.set('minPrice', Number(filters.minPrice) / rate);
            if (filters.maxPrice) params.set('maxPrice', Number(filters.maxPrice) / rate);
            if (filters.scope === 'local' && filters.maxDistance) params.set('maxDistance', filters.maxDistance);
            if (sort !== 'recommended') params.set('sort', sort);
            if (buyerPoint) {
                params.set('lat', buyerPoint.lat);
                params.set('lon', buyerPoint.lon);
            }
            // Pass buyer's country so backend can filter by visibility
            if (buyerCountry) params.set('buyerCountry', buyerCountry);
        }

        try {
            if (isB2BMode) {
                const response = await axios.get(
                    `${process.env.REACT_APP_Backend_Host_Name}/api/v1/b2b/listings?${params.toString()}`
                );
                if (response.data.success) {
                    setProducts(response.data.listings || []);
                }
            } else {
                const response = await axios.get(
                    `${process.env.REACT_APP_Backend_Host_Name}/api/v1/products/filtered?${params.toString()}`
                );
                if (response.data.success) {
                    const transformedProducts = response.data.products.map(transformProduct);
                    setProducts(transformedProducts);
                }
            }
        } catch (error) {
            console.error('Error fetching products:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchProducts();
    }, [filters, isB2BMode, sort, buyerPoint?.lat, buyerPoint?.lon, exchangeRate]);

    useEffect(() => {
        if (isB2BMode || savedBuyerPoint(user)) return;
        getBrowserLocation().then((point) => point && setBrowserPoint(point));
    }, [isB2BMode, user]);

    useEffect(() => {
        setPriceDraft({ min: filters.minPrice, max: filters.maxPrice });
    }, [filters.minPrice, filters.maxPrice]);

    useEffect(() => {
        setDistanceDraft(Number(filters.maxDistance) || MAX_LOCAL_KM);
    }, [filters.maxDistance]);

    useEffect(() => {
        if (!isB2BMode) {
            setB2bCategories([]);
            return;
        }

        axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/categories?b2b=true`)
            .then((response) => setB2bCategories(response.data.categories || []))
            .catch(() => setB2bCategories([]));
    }, [isB2BMode]);

    const handleSearch = (query) => {
        setSearchQuery(query);
        fetchProducts(query);
    };

    const ratingOptions = [
        { value: 'notRated', label: 'Not rated' },
        { value: 5, label: '5 Stars' },
        { value: 4, label: '4 Stars & Above' },
        { value: 3, label: '3 Stars & Above' },
        { value: 2, label: '2 Stars & Above' },
        { value: 1, label: '1 Star & Above' },
    ];

    const removeFilter = (key) => {
        setFilters({
            ...filters,
            [key]: Array.isArray(filters[key]) ? [] : '',
            ...(key === 'scope' && { maxDistance: '' }),
        });
    };

    const applyPrice = () => {
        const clean = (v) => (v === '' || Number(v) < 0 ? '' : String(v));
        const min = clean(priceDraft.min);
        const max = clean(priceDraft.max);
        if (min !== filters.minPrice || max !== filters.maxPrice) setFilters({ ...filters, minPrice: min, maxPrice: max });
    };

    const applyDistance = (km) => {
        const value = km >= MAX_LOCAL_KM ? '' : String(km);
        if (value !== filters.maxDistance) setFilters({ ...filters, maxDistance: value });
    };

    const sortOptions = [
        { value: 'recommended', label: 'Recommended' },
        ...(buyerPoint && filters.scope !== 'international' ? [{ value: 'closest', label: 'Closest first' }] : []),
        { value: 'price_asc', label: 'Price: low to high' },
        { value: 'price_desc', label: 'Price: high to low' },
        { value: 'rating', label: 'Highest rated' },
        { value: 'orders', label: 'Most orders' },
    ];

    const chipValue = (key, value) => {
        if (Array.isArray(value)) return value.join(', ');
        if (key === 'minRating') return value === 'notRated' ? 'Not rated' : ratingOptions.find((o) => String(o.value) === String(value))?.label || value;
        if (key === 'minPrice' || key === 'maxPrice') return `${currency || 'USD'} ${value}`;
        if (key === 'maxDistance') return `Within ${value} km`;
        if (key === 'scope') return SCOPE_OPTIONS.find((o) => o.value === value)?.label || value;
        return value;
    };

    const clearAllFilters = () => {
        setFilters({
            category: '',
            subcategory: '',
            childcategory: '',
            minPrice: '',
            maxPrice: '',
            deliveryType: [],
            minRating: '',
            sellerCountry: '',
            maxDistance: '',
            scope: '',
        });
        setSearchQuery('');
    };

    const toggleFilterSidebar = () => {
        setIsFilterOpen(!isFilterOpen);
    };

    const closeFilterSidebar = () => {
        setIsFilterOpen(false);
    };

    const hasActiveFilters = Boolean(searchQuery) || Object.values(filters).some((value) => {
        if (Array.isArray(value)) return value.length > 0;
        return Boolean(value);
    });

    const seoTitle = isB2BMode ? 'B2B Products & Services' : 'Products & Services';
    const seoDescription = isB2BMode
        ? 'Browse wholesale listings from verified suppliers. Compare MOQ, pricing tiers, and shipping options across categories.'
        : 'Explore products and services from trusted sellers. Filter by category, rating, and delivery options on Chocowi.';
    const canonicalPath = isB2BMode ? '/b2b/products-services' : '/products-services';

    // Close sidebar when screen size increases beyond 768px
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > 768 && isFilterOpen) {
                setIsFilterOpen(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isFilterOpen]);

    const renderFilters = () => (
        <div className="filtersContent">
            <div>
                <label>Category</label>
                <Dropdown
                    options={categorySource.map((cat) => ({ value: cat.name, label: cat.name }))}
                    value={filters.category ? { value: filters.category, label: filters.category } : null}
                    onChange={(selected) =>
                        setFilters({ ...filters, category: selected?.value || '', subcategory: '', childcategory: '' })
                    }
                    placeholder="Select Category"
                    isClearable
                />
            </div>
            <div>
                <label>Subcategory</label>
                <Dropdown
                    options={
                        categorySource
                            .find((cat) => cat.name === filters.category)
                            ?.subcategories?.map((sub) => ({ value: sub.name, label: sub.name })) || []
                    }
                    value={filters.subcategory ? { value: filters.subcategory, label: filters.subcategory } : null}
                    onChange={(selected) => setFilters({ ...filters, subcategory: selected?.value || '', childcategory: '' })}
                    placeholder="Select Subcategory"
                    isClearable
                    disabled={!filters.category}
                />
            </div>
            <div>
                <label>Child Category</label>
                <Dropdown
                    options={
                        categorySource
                            .find((cat) => cat.name === filters.category)
                            ?.subcategories?.find((sub) => sub.name === filters.subcategory)
                            ?.children?.map((child) => ({ value: child.name, label: child.name })) || []
                    }
                    value={filters.childcategory ? { value: filters.childcategory, label: filters.childcategory } : null}
                    onChange={(selected) => setFilters({ ...filters, childcategory: selected?.value || '' })}
                    placeholder="Select Child Category"
                    isClearable
                    disabled={!filters.subcategory}
                />
            </div>
            {!isB2BMode && (
                <>
                    <div>
                        <label>Listing Type</label>
                        <Dropdown
                            options={SCOPE_OPTIONS}
                            value={SCOPE_OPTIONS.find((o) => o.value === filters.scope) || null}
                            onChange={(selected) => setFilters({ ...filters, scope: selected?.value || '', maxDistance: '' })}
                            placeholder="All listings"
                            isClearable
                        />
                    </div>
                    <div>
                        <label>Price ({currency || 'USD'})</label>
                        <div className="priceRangeInputs">
                            {['min', 'max'].map((bound) => (
                                <input
                                    key={bound}
                                    type="number"
                                    min="0"
                                    className="priceRangeInput"
                                    placeholder={bound === 'min' ? 'Min' : 'Max'}
                                    value={priceDraft[bound]}
                                    onChange={(e) => setPriceDraft({ ...priceDraft, [bound]: e.target.value })}
                                    onBlur={applyPrice}
                                    onKeyDown={(e) => e.key === 'Enter' && applyPrice()}
                                />
                            ))}
                        </div>
                    </div>
                    {filters.scope === 'local' && (
                        <div className="distanceFilter">
                            <label>
                                Distance: <strong>{distanceDraft >= MAX_LOCAL_KM ? `Up to ${MAX_LOCAL_KM} km` : `Within ${distanceDraft} km`}</strong>
                            </label>
                            {buyerPoint ? (
                                <>
                                    <input
                                        type="range"
                                        min="5"
                                        max={MAX_LOCAL_KM}
                                        step="5"
                                        value={distanceDraft}
                                        onChange={(e) => setDistanceDraft(Number(e.target.value))}
                                        onMouseUp={(e) => applyDistance(Number(e.target.value))}
                                        onTouchEnd={(e) => applyDistance(Number(e.target.value))}
                                        onKeyUp={(e) => applyDistance(Number(e.target.value))}
                                        className="distanceSlider"
                                    />
                                    <div className="distancePresets">
                                        {DISTANCE_PRESETS.map((km) => (
                                            <button
                                                key={km}
                                                type="button"
                                                className={`distancePreset ${distanceDraft === km ? 'active' : ''}`}
                                                onClick={() => { setDistanceDraft(km); applyDistance(km); }}
                                            >
                                                {km}
                                            </button>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <p className="distanceHint">Set your delivery address or allow location access to filter by distance.</p>
                            )}
                        </div>
                    )}
                </>
            )}
            <div>
                <label>Rating</label>
                <Dropdown
                    options={ratingOptions}
                    value={
                        filters.minRating
                            ? ratingOptions.find((o) => o.value === filters.minRating) || { value: filters.minRating, label: String(filters.minRating) }
                            : null
                    }
                    onChange={(selected) => setFilters({ ...filters, minRating: selected?.value || '' })}
                    placeholder="Select Rating"
                    isClearable
                />
            </div>
            <div>
                <label>Seller Country</label>
                <Dropdown
                    options={[{ value: 'Worldwide', label: 'Worldwide' }, ...countries.map((country) => ({ value: country.name, label: country.name }))]}
                    value={filters.sellerCountry ? { value: filters.sellerCountry, label: filters.sellerCountry } : null}
                    onChange={(selected) => setFilters({ ...filters, sellerCountry: selected?.value || '' })}
                    placeholder="Select Country"
                    isClearable
                />
            </div>
        </div>
    );

    return (
        <div className="productsPage">
            <SEO
                title={seoTitle}
                description={seoDescription}
                canonicalPath={canonicalPath}
                noIndex={hasActiveFilters}
                noFollow={false}
            />
            <section className="mainSection">
                <SearchBar
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    onSearch={handleSearch}
                    enableFilters={false}
                />
                <button className="filterButton" onClick={toggleFilterSidebar}>
                    Filters
                </button>
                <div className="filtersContainer desktopFilters">{renderFilters()}</div>
                <div className={`filtersSidebar ${isFilterOpen ? 'open' : ''}`}>
                    <div className="sidebarHeader">
                        <h2>Filters</h2>
                        <button className="closeButton" onClick={closeFilterSidebar}>
                            ✕
                        </button>
                    </div>
                    {renderFilters()}
                </div>
                <div className="appliedFiltersWrap">
                    {Object.entries(filters).map(([key, value]) => {
                        if (!value || (Array.isArray(value) && value.length === 0)) return null;
                        if (key === 'maxDistance' && filters.scope !== 'local') return null;
                        const labelMap = { category: 'Category', subcategory: 'Subcategory', childcategory: 'Child category', minPrice: 'Min price', maxPrice: 'Max price', deliveryType: 'Delivery', minRating: 'Rating', sellerCountry: 'Country', maxDistance: 'Distance', scope: 'Listing type' };
                        const label = labelMap[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase());
                        const displayValue = chipValue(key, value);
                        return (
                            <span key={key} className="appliedFilterChip">
                                <span className="appliedFilterChipLabel">{label}:</span>
                                <span className="appliedFilterChipValue">{displayValue}</span>
                                <button type="button" className="appliedFilterChipRemove" onClick={() => removeFilter(key)} aria-label={`Remove ${label} filter`}>×</button>
                            </span>
                        );
                    })}
                    {Object.entries(filters).some(([k, v]) => v && (Array.isArray(v) ? v.length > 0 : true)) && (
                        <button type="button" className="appliedFiltersClearAll" onClick={clearAllFilters}>
                            Clear all filters
                        </button>
                    )}
                </div>
                <div className="totalItemsSort">
                    <p>{products.length} results{isB2BMode ? ' in B2B Marketplace' : ''}</p>
                    {!isB2BMode && (
                        <div className="sortDropdown">
                            <label>Sort by</label>
                            <Dropdown
                                options={sortOptions}
                                value={sortOptions.find((o) => o.value === sort) || sortOptions[0]}
                                onChange={(selected) => setSort(selected?.value || 'recommended')}
                            />
                        </div>
                    )}
                </div>
                {!isLoading ? (
                    <div className="filteredProducts">
                        {products.map((product) => (
                            isB2BMode
                                ? <ProductCard key={product._id} b2bListing={product} />
                                : <ProductCard key={product._id} product={product} />
                        ))}
                    </div>
                ) : (
                    <Loader type="simpleMini" />
                )}
            </section>
        </div>
    );
};

export default Products;